import { Inject, Injectable } from '@angular/core';
import { CanActivate, CanActivateChild, Router, UrlTree } from '@angular/router';
import {
	AppSettingsService,
	CustomWindow,
	FeatureService,
	LoggingService,
	ShopperService,
	ShopperState,
	WINDOW,
} from '@woolworthsnz/styleguide';
import { ContextResponse } from '@woolworthsnz/trader-api';
import { Observable } from 'rxjs';
import { map, skipWhile, tap } from 'rxjs/operators';

@Injectable({
	providedIn: 'root',
})
export class MandatoryLoginGuard implements CanActivate, CanActivateChild {
	constructor(
		private shopperService: ShopperService,
		private router: Router,
		@Inject(WINDOW) public window: CustomWindow,
		private appSettingsService: AppSettingsService,
		private loggingService: LoggingService,
		private featureService: FeatureService
	) {}
	canActivateChild(): Observable<boolean | UrlTree> {
		return this.canActivate();
	}

	canActivate(): Observable<boolean | UrlTree> {
		return this.shopperService.state$.pipe(
			tap((s) => {
				if (this.featureService.isFeatureEnabled(ContextResponse.EnabledFeaturesEnum.RedirectValidation)) {
					this.loggingService.trackEvent('RedirectValidation', {
						shopper: this.shopperService.state?.shopperScvId || 'unknown',
						detail: 'before MandatoryLoginGuard',
						isLoggedIn: s.isLoggedIn,
						isEmbeddedInApp: this.appSettingsService.settings.isEmbeddedInApp,
					});
				}
			}),
			skipWhile((s) => s.isLoggedIn === undefined),
			map((s: ShopperState) => {
				if (this.featureService.isFeatureEnabled(ContextResponse.EnabledFeaturesEnum.RedirectValidation)) {
					this.loggingService.trackEvent('RedirectValidation', {
						shopper: this.shopperService.state?.shopperScvId || 'unknown',
						detail: 'after MandatoryLoginGuard',
						isLoggedIn: s.isLoggedIn,
						isEmbeddedInApp: this.appSettingsService.settings.isEmbeddedInApp,
					});
				}

				if (s.isLoggedIn || !this.isMandatoryLoginRequired()) {
					return true;
				}
				const redirectUrl = this.window.location.href;
				const loginUrl = `${this.appSettingsService.getEndpoint(
					'oidcSignInEndpoint'
				)}?redirectUrl=${encodeURIComponent(this.appSettingsService.getAuthRedirectUrl(redirectUrl))}`;

				if (this.featureService.isFeatureEnabled(ContextResponse.EnabledFeaturesEnum.RedirectValidation)) {
					this.loggingService.trackEvent('RedirectValidation', {
						detail: 'redirect in MandatoryLoginGuard',
						href: loginUrl,
					});
				}

				this.window.location.href = loginUrl;
				return false;
			})
		);
	}

	isMandatoryLoginRequired(): boolean {
		return this.appSettingsService.settings.isEmbeddedInApp;
	}
}
